import type { InvoiceModel } from '@/Models/InvoiceModel';
import type PatientStatus from '@/Models/PatientStatus';
import Autocomplete, { type AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import debounce from 'lodash/debounce';
import { useCallback, useMemo, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import * as API from '../../API';
import type Nomenclature from '../../Models/Nomenclature';
import type OrderNomenclature from '../../Models/OrderNomenclature';
import type Procedure from '../../Models/Procedure';
import type ProcedureNomenclature from '../../Models/ProcedureNomenclature';
import Styles from './SearchNomenclatureGrid.module.scss';

const getNomenclaturesForSearchTerm = async (searchTerm: string, patientStatus: PatientStatus, atDateTimeString: string, procedures: Procedure[]): Promise<ProcedureNomenclature[]> => {
    if (searchTerm === '') {
        return [];
    }

    const nomenclaturesWithTerm = await API.getNomenclatureForTerm(searchTerm, patientStatus, atDateTimeString);
    const allNomenclaturesProcedureCombos = procedures.flatMap((procedure) => nomenclaturesWithTerm.map((nomenclature) => ({ Nomenclature: nomenclature, Procedure: procedure } satisfies ProcedureNomenclature)));

    return allNomenclaturesProcedureCombos;
};

function SearchNomenclatureGrid({ invoiceModel, procedures, nomenclatures, changeNomenclatures }: Readonly<{
    invoiceModel: InvoiceModel; procedures: Procedure[]; nomenclatures: OrderNomenclature[]; changeNomenclatures: Function;
}>): React.ReactElement {
    const [searchedNomens, setSearchedNomens] = useState<ProcedureNomenclature[]>([]);
    const { t } = useTranslation();

    const onInputChangeEventHandler = useCallback((event: SyntheticEvent<Element, Event>, value: string, reason: AutocompleteInputChangeReason): void => {
        void getNomenclaturesForSearchTerm(value, invoiceModel.PatientStatus, invoiceModel.DateOfImplementationString, procedures)
            .then((results) => setSearchedNomens(results))
            .catch(() => console.error('Something went wrong while getting nomenclatures for term'));
    }, [invoiceModel.PatientStatus, invoiceModel.DateOfImplementationString, procedures]);

    const debouncedOnInputChangeEventHandler = useMemo(() => debounce(onInputChangeEventHandler, 500), [onInputChangeEventHandler]);

    const addManualNomenclature = (nomen: Nomenclature, proc: Procedure): void => {
        const nomenclaturesCopy = [...nomenclatures];
        nomenclaturesCopy.push({
            ID: nomen.ID,
            Number: nomen.Number,
            IsProduct: nomen.IsProduct,
            CustomHonorarium: 0,
            CustomWIGW: 0,
            CustomOther: 0,
            CustomPractice: 0,
            IsCustomAmountSupplement: false,
            IsExtraSupplement: false,
            CanBeMotivated: nomen.CanBeMotivated,
            MotivationType: nomen.MotivationType,
            Honorarium: nomen.Honorarium,
            WIGW: nomen.WIGW,
            Practice: nomen.Practice,
            Other: nomen.Other,
            Description: nomen.Description,
            IsSupplement: nomen.IsSupplement,
            SupplementType: nomen.SupplementType,
            IsPseudo: nomen.IsPseudo,
            ProcedureCode: proc.Code,
            IsScreening: nomen.IsScreening,
            Remarks: nomen.Remarks,
            IsManuallyAdded: true,
        });
        changeNomenclatures(nomenclaturesCopy);
    };

    return (
        <Grid
            size={12}
            className={Styles.searchNomenclatureContainer}
            id="searchNomenclature"
        >
            <Autocomplete
                className={`dis ${Styles.searchBox}`}
                onInputChange={debouncedOnInputChangeEventHandler}
                options={searchedNomens}
                getOptionLabel={(option) => `${option.Nomenclature.Number} - ${option.Procedure.Modality_Text} ${option.Procedure.Code}`}
                renderOption={(props, option) => (
                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        {t('AddNomenclature')}
                        {' '}
                        {option.Nomenclature.Number}
                        {' '}
                        {t('to')}
                        <p className="liExample" style={{ backgroundColor: option.Procedure.Color }}>
                            <span className="insideProcedureItemContainer">
                                <b>{option.Procedure.Modality_Text}</b>
                                {' '}
                                {option.Procedure.Code}
                            </span>
                        </p>
                    </Box>
                )}
                renderInput={(params) => <TextField {...params} label={t('AddNomenclature')} />}
                onChange={(e, value) => {
                    if (value?.Nomenclature) {
                        addManualNomenclature(value.Nomenclature, value.Procedure);
                    }
                }}
            />
        </Grid>
    );
}
export default SearchNomenclatureGrid;
